/**
 * WeatherCommanderPage · v2.7.30 (2026-02-21)
 * ===========================================
 * Unified command center for NWS / state emergency alerts. Routed at
 * /admin/weather with five internal tabs:
 *
 *   · Dashboard Overview     — aggregated radar + tickers + state alerts
 *   · Live Weather Radar     — Regional (OK 77 counties) + National toggle
 *   · NWS Discussions        — text-based AFD product feed
 *   · Active Warnings        — filterable polygon list
 *   · Dispatch Warning       — operator manual broadcast console
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, RefreshCw, Cloud, Radio, AlertTriangle, MessageSquare, Send, VolumeX, Volume2, Activity, Play, Pause, Maximize2, Minimize2, SkipBack, SkipForward, BookOpen, ShieldAlert, ShieldCheck, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import useWeatherStream from "@/hooks/useWeatherStream";
import { playWeatherAlarm, stopWeatherAlarm, isWeatherAlarmActive } from "@/lib/weatherAlarm";
import { NWS_INFO, nwsBadgeCls, nwsStroke, nwsFill, isWatch, isWarning } from "@/lib/nwsColors";
import { rainviewerTileUrl } from "@/lib/rainviewerTile";
import { loadGoogleMaps } from "@/lib/loadGoogleMaps";
import StateEmergenciesPage from "@/pages/admin/StateEmergenciesPage";
import ExtendedForecastPanel from "@/components/admin/ExtendedForecastPanel";

const TABS = [
  { id: "overview",         label: "Dashboard Overview",            Icon: Activity },
  { id: "radar",            label: "Live Weather Radar",            Icon: Radio },
  { id: "extended",         label: "8-Day · 12-Day Forecast",       Icon: CalendarDays },
  { id: "discussions",      label: "NWS Discussions (raw)",         Icon: MessageSquare },
  { id: "nws-discussions",  label: "NWS Forecast Discussions",      Icon: BookOpen },
  { id: "warnings",         label: "Active Warnings",               Icon: AlertTriangle },
  { id: "dispatch",         label: "Dispatch Warning",              Icon: Send },
  { id: "state-emergencies", label: "State Emergencies",            Icon: ShieldAlert },
];

// v2.7.32 · severity classification now lives in /lib/nwsColors.js so the
// SAME palette is shared by the radar polygons, the warnings list, the
// dispatch-map triangles, and the OUN-AFD structured view.
const severityCls = (event = "", severity = "") => nwsBadgeCls(event, severity);

const fmtExpiry = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const mins = Math.round((d - now) / 60000);
    if (mins < 0) return "expired";
    if (mins < 60) return `${mins}m left`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m left`;
  } catch { return iso; }
};

// ────────────────────── Page shell + tab nav ──────────────────────
export default function WeatherCommanderPage() {
  const readTab = () => {
    const h = (window.location.hash || "").replace(/^#/, "");
    const m = new URLSearchParams(h).get("sub");
    return TABS.find((t) => t.id === m) ? m : "overview";
  };
  const [active, setActive] = useState(readTab());
  useEffect(() => {
    const onHash = () => setActive(readTab());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const setTab = (id) => {
    const h = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
    h.set("sub", id);
    window.history.replaceState(null, "", `${window.location.pathname}#${h.toString()}`);
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  };

  // Mount the global weather listener — fires the EAS tone on push alerts.
  useWeatherStream({ enabled: true, audibleAlarm: true });

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100" data-testid="weather-commander-page">
      <div className="border-b border-zinc-800 bg-zinc-950">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider" data-testid="weather-back">
              <ArrowLeft className="h-4 w-4"/> Admin Console
            </Link>
            <div className="h-6 w-px bg-zinc-800"/>
            <Cloud className="h-6 w-6 text-sky-300"/>
            <h1 className="text-2xl font-extrabold tracking-tight">Weather Commander</h1>
          </div>
          <AlarmToggle/>
        </div>
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 pb-2 flex flex-wrap gap-1">
          {TABS.map((t) => {
            const Icon = t.Icon;
            const isOn = t.id === active;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${isOn ? "text-amber-300 border-b-amber-400" : "text-zinc-400 hover:text-amber-200 border-b-zinc-700/40"}`}
                data-testid={`weather-tab-${t.id}`}
              >
                <span className="flex items-center gap-2">
                  <Icon className="h-3.5 w-3.5"/>
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="max-w-[1800px] mx-auto p-4 sm:p-6">
        {active === "overview"          && <DashboardOverview onJumpToTab={setTab}/>}
        {active === "radar"             && <RadarPanel/>}
        {active === "extended"          && <ExtendedForecastPanel/>}
        {active === "discussions"       && <DiscussionsPanel/>}
        {active === "nws-discussions"   && <OUNAreaForecastPanel/>}
        {active === "oun-afd"           && <OUNAreaForecastPanel/>}
        {active === "warnings"          && <WarningsPanel/>}
        {active === "dispatch"          && <DispatchWarningPanel/>}
        {active === "state-emergencies" && <StateEmergenciesEmbed/>}
      </div>
    </div>
  );
}

function AlarmToggle() {
  const [muted, setMuted] = useState(false);
  return (
    <div className="flex items-center gap-3">
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          if (muted) {
            setMuted(false);
            playWeatherAlarm();
            toast.success("EAS alarm tone test fired");
          } else {
            stopWeatherAlarm();
            setMuted(true);
          }
        }}
        className={muted ? "bg-zinc-950 border-zinc-700 text-zinc-300" : "bg-amber-400/10 border-amber-400/40 text-amber-300"}
        data-testid="weather-alarm-toggle"
      >
        {muted ? <VolumeX className="h-4 w-4 mr-1"/> : <Volume2 className="h-4 w-4 mr-1"/>}
        {muted ? "Test EAS tone" : "Mute"}
      </Button>
    </div>
  );
}

// ────────────────────── Tab: Dashboard Overview ──────────────────────
function DashboardOverview({ onJumpToTab }) {
  const [alerts, setAlerts] = useState([]);
  const [stateAlerts, setStateAlerts] = useState([]);
  // v2.7.35 · Pull the CMS config so the Dashboard Overview header
  // shows the same "Taxi Weather Commander" logo the public page uses.
  const [cms, setCms] = useState(null);
  useEffect(() => {
    adminApi.get("/admin/weather/cms/config").then((r) => setCms(r.data)).catch(() => {});
  }, []);
  const reload = useCallback(() => {
    adminApi.get("/admin/weather/alerts/active").then((r) => setAlerts(r.data || [])).catch(() => {});
    adminApi.get("/admin/weather/alerts/state").then((r) => setStateAlerts(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { reload(); const id = setInterval(reload, 10000); return () => clearInterval(id); }, [reload]);
  useEffect(() => {
    const onAlert = () => reload();
    window.addEventListener("rtg:weather_alert", onAlert);
    return () => window.removeEventListener("rtg:weather_alert", onAlert);
  }, [reload]);

  const warnings = alerts.filter((a) => /Warning/i.test(a.event));
  const watches  = alerts.filter((a) => /Watch/i.test(a.event));
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const logoUrl = cms?.logo_url ? `${apiBase}${cms.logo_url}` : null;

  return (
    <div className="space-y-4">
      {/* v2.7.35 · Branded header block — "Taxi Weather Commander" logo */}
      <Card className="bg-gradient-to-r from-zinc-900 via-sky-950/40 to-zinc-900 border-sky-400/30 p-5" data-testid="wc-header-block">
        <div className="flex items-center gap-4 flex-wrap">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Taxi Weather Commander"
              className="max-w-[220px] max-h-[72px] object-contain bg-white/5 rounded p-1"
              data-testid="wc-header-logo"
            />
          ) : (
            <div className="flex items-center gap-2 text-sky-300">
              <Cloud className="h-8 w-8"/>
              <span className="text-2xl font-extrabold tracking-tight">Weather Commander</span>
            </div>
          )}
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold">Dashboard Overview</div>
            <div className="text-sm text-zinc-300 mt-1 leading-relaxed">{cms?.description || "Live OK weather radar, NWS warnings, and dispatch alert pipeline."}</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="bg-zinc-950 border-zinc-700 text-xs h-8" onClick={() => onJumpToTab("radar")} data-testid="wc-header-radar">Live Radar →</Button>
            <Button size="sm" variant="outline" className="bg-zinc-950 border-zinc-700 text-xs h-8" onClick={() => onJumpToTab("warnings")} data-testid="wc-header-warnings">Warnings →</Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-red-400/40 flex items-center justify-between">
          <span className="flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5 text-red-300"/>Active Warnings</span>
          <Badge className="bg-red-500/15 text-red-300 border-red-400/40">{warnings.length}</Badge>
        </div>
        {warnings.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center">No active warnings.</div>}
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {warnings.map((a) => <AlertRow key={a.id} alert={a}/>)}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-amber-400/40 flex items-center justify-between">
          <span className="flex items-center gap-2"><Cloud className="h-3.5 w-3.5 text-amber-300"/>Active Watches</span>
          <Badge className="bg-amber-500/15 text-amber-300 border-amber-400/40">{watches.length}</Badge>
        </div>
        {watches.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center">No active watches.</div>}
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {watches.map((a) => <AlertRow key={a.id} alert={a} compact/>)}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-zinc-700/40 flex items-center justify-between">
          <span>Regional Radar Snapshot</span>
          <Button size="sm" variant="outline" className="bg-zinc-950 border-zinc-700 text-xs h-7" onClick={() => onJumpToTab("radar")} data-testid="overview-open-radar">Open Radar →</Button>
        </div>
        <RadarPreview/>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-fuchsia-400/40 flex items-center justify-between">
          <span>State Emergencies</span>
          <Badge className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/40">{stateAlerts.length}</Badge>
        </div>
        {stateAlerts.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center">No active state alerts.</div>}
        <div className="space-y-2 max-h-[260px] overflow-y-auto">
          {stateAlerts.slice(0, 8).map((a) => <StateAlertRow key={a.id} alert={a}/>)}
        </div>
      </Card>
      </div>
    </div>
  );
}

function AlertRow({ alert, compact = false }) {
  const cls = severityCls(alert.event, alert.severity);
  const expires = fmtExpiry(alert.expires || alert.ends);
  return (
    <div className={`rounded p-3 border ${cls} bg-zinc-950/40`} data-testid={`alert-row-${alert.id?.slice(-12)}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="font-bold text-sm">{alert.event}</span>
        <span className="text-[10px] uppercase tracking-wider opacity-75 tabular-nums">{expires}</span>
      </div>
      {!compact && alert.headline && <div className="text-xs text-zinc-300 mt-1.5">{alert.headline}</div>}
      <div className="text-[10px] text-zinc-500 mt-1">{alert.areaDesc?.slice(0, 140)}</div>
    </div>
  );
}

function StateAlertRow({ alert }) {
  const cat = alert.state_category || "ALERT";
  const tint = {
    AMBER: "bg-amber-500/15 text-amber-300 border-amber-400/50",
    SILVER: "bg-slate-500/15 text-slate-300 border-slate-400/50",
    BLUE: "bg-blue-500/15 text-blue-300 border-blue-400/50",
    KASEY: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/50",
    ALERT: "bg-red-500/15 text-red-300 border-red-400/50",
  }[cat] || "bg-zinc-700/30 text-zinc-300 border-zinc-700";
  return (
    <div className={`rounded p-3 border ${tint} bg-zinc-950/40`}>
      <div className="flex items-center justify-between gap-2">
        <Badge className={tint + " font-bold uppercase text-[10px]"}>{cat}</Badge>
        <span className="text-[10px] opacity-75">{fmtExpiry(alert.expires)}</span>
      </div>
      <div className="text-xs text-zinc-200 mt-1.5 font-semibold">{alert.event}</div>
      <div className="text-[10px] text-zinc-400 mt-0.5 line-clamp-2">{alert.headline?.slice(0, 200)}</div>
    </div>
  );
}

// ────────────────────── Tab: Live Weather Radar ──────────────────────
function RadarPanel() {
  const [view, setView] = useState("regional");
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-3 sm:p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 pb-2 mb-3 border-b border-sky-400/40">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-2">
          <Radio className="h-3.5 w-3.5 text-sky-300"/>
          Live Weather Radar · {view === "regional" ? "Regional (Oklahoma)" : "Continental (CONUS)"}
        </div>
        <div className="flex items-center gap-1 bg-zinc-950 rounded p-1 border border-zinc-800">
          <button
            type="button"
            onClick={() => setView("regional")}
            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition-colors ${view === "regional" ? "bg-sky-400/20 text-sky-200" : "text-zinc-400 hover:text-zinc-200"}`}
            data-testid="radar-tab-regional"
          >Regional</button>
          <button
            type="button"
            onClick={() => setView("national")}
            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition-colors ${view === "national" ? "bg-sky-400/20 text-sky-200" : "text-zinc-400 hover:text-zinc-200"}`}
            data-testid="radar-tab-national"
          >National</button>
        </div>
      </div>
      <RadarMap mode={view}/>
      <div className="mt-3 text-[10px] text-zinc-500 flex items-center gap-4 flex-wrap">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#FF0000" }}/>Tornado Warning</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#FFA500" }}/>Severe T-storm Warning</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#00FF00" }}/>Flash Flood Warning</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#FFFF00" }}/>Tornado Watch</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#DB7093" }}/>Severe T-storm Watch</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#FF00FF" }}/>Tornado Emergency</span>
        <span className="ml-auto">Radar: RainViewer (10-min frames · live + nowcast) · Colors: NWS standard · refresh 60s</span>
      </div>
    </Card>
  );
}

function RadarPreview() {
  return (
    <div className="aspect-[16/9] w-full rounded-lg overflow-hidden border border-zinc-800">
      <RadarMap mode="regional" compact/>
    </div>
  );
}

function RadarMap({ mode = "regional", compact = false }) {
  const wrapperRef = useRef(null);
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const countyLayerRef = useRef([]);
  const warningLayerRef = useRef([]);
  // v2.7.31 · Animated radar (RainViewer) — frames + playback state
  const overlaysRef = useRef([]); // ImageMapType[] — one per frame, stacked, only ONE visible at a time
  const [framesPast, setFramesPast] = useState([]);
  const [framesFuture, setFramesFuture] = useState([]);
  const [rvHost, setRvHost] = useState("");
  const [frameIdx, setFrameIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speedMs, setSpeedMs] = useState(500);
  const [opacity, setOpacity] = useState(0.7);
  const [fullscreen, setFullscreen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  // v2.7.34 · Live vs Future radar mode toggle. RainViewer's `nowcast`
  // payload provides 0-3 short-range forecast frames (~30 minutes of
  // forward-looking precipitation extrapolation). For deeper futures
  // we'd need HRRR tiles which aren't publicly served; the UI is built
  // to swap manifests when long-range tiles become available.
  const [radarMode, setRadarMode] = useState("live"); // "live" | "future"
  const frames = useMemo(() => (radarMode === "future" ? framesFuture : framesPast), [radarMode, framesPast, framesFuture]);

  // Load county GeoJSON once
  const [countiesGJ, setCountiesGJ] = useState(null);
  useEffect(() => {
    fetch("/data/oklahoma-counties.geojson")
      .then((r) => r.json())
      .then(setCountiesGJ)
      .catch(() => setCountiesGJ(null));
  }, []);

  // Load alerts (active warnings/watches)
  const reload = useCallback(() => {
    adminApi.get("/admin/weather/alerts/active").then((r) => setAlerts(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { reload(); const id = setInterval(reload, 10000); return () => clearInterval(id); }, [reload]);
  useEffect(() => {
    const onAlert = () => reload();
    window.addEventListener("rtg:weather_alert", onAlert);
    return () => window.removeEventListener("rtg:weather_alert", onAlert);
  }, [reload]);

  // v2.7.31 · Fetch RainViewer animated radar manifest (refreshed every 5 min)
  // v2.7.34 · Split past + nowcast (future) into separate arrays so the
  // operator can flip between Live / Future Radar modes without losing
  // either timeline.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetch("https://api.rainviewer.com/public/weather-maps.json")
        .then((r) => r.json())
        .then((d) => {
          if (cancelled) return;
          setRvHost(d.host || "");
          const past = (d.radar?.past || []).slice(-12);
          const nowcast = (d.radar?.nowcast || []).slice(0, 6);
          setFramesPast([...past, ...nowcast]);                 // Live = past+short nowcast (default)
          // v2.7.35 · P1 fix · `Future` ONLY populated when nowcast frames truly
          // exist. Empty array → Future toggle disables (via hasFuture=false in
          // RadarControls). Removes the misleading "past.slice(-3)" fallback
          // that made hasFuture always truthy.
          setFramesFuture(nowcast);
          // Default cursor in Live mode = most recent past frame
          setFrameIdx(Math.max(0, past.length - 1));
        })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // v2.7.34 · Reset cursor when switching between Live and Future modes
  useEffect(() => {
    setFrameIdx(0);
  }, [radarMode]);

  // Init Google Maps
  // v2.7.38 · Use the singleton loader so the map ALWAYS initializes once
  // the script becomes available — previously the effect bailed out
  // (`if (!window.google) return`) and never retried, leaving the radar
  // pane empty for users who navigated directly to /admin/weather#sub=radar
  // before any other map-bearing page had injected the API. That race
  // condition was the production "live weather radar shows no tiles" bug.
  useEffect(() => {
    let cancelled = false;
    if (!mapDivRef.current) return undefined;
    if (mapRef.current) return undefined;
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapDivRef.current || mapRef.current) return;
        const center = mode === "national" ? { lat: 39.5, lng: -98.5 } : { lat: 35.5, lng: -97.5 };
        const zoom = mode === "national" ? 4 : (compact ? 6 : 7);
        mapRef.current = new maps.Map(mapDivRef.current, {
          center,
          zoom,
          mapTypeId: "terrain",
          streetViewControl: false,
          fullscreenControl: false, // we supply our own (works in our wrapper, with controls visible)
          mapTypeControl: !compact,
          styles: [
            { featureType: "all", elementType: "labels", stylers: [{ visibility: "simplified" }] },
          ],
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[WeatherCommander] failed to load Google Maps:", err);
      });
    return () => { cancelled = true; };
  }, [mode, compact]);

  // Re-center on mode change
  useEffect(() => {
    if (!mapRef.current) return;
    const center = mode === "national" ? { lat: 39.5, lng: -98.5 } : { lat: 35.5, lng: -97.5 };
    const zoom = mode === "national" ? 4 : (compact ? 6 : 7);
    mapRef.current.setCenter(center);
    mapRef.current.setZoom(zoom);
  }, [mode, compact]);

  // v2.7.31 · Build N overlay layers (one per frame) when frames/host change.
  // All layers are stacked; we hide all and reveal one via setOpacity below.
  useEffect(() => {
    if (!mapRef.current || !rvHost || frames.length === 0) return;
    // Tear down previous overlays from the map's overlayMapTypes stack
    const stack = mapRef.current.overlayMapTypes;
    for (const old of overlaysRef.current) {
      const idx = stack.getArray().indexOf(old);
      if (idx >= 0) stack.removeAt(idx);
    }
    overlaysRef.current = frames.map((f, i) => {
      const layer = new window.google.maps.ImageMapType({
        // v2.7.34 · Parent-tile stretching keeps radar visible at street
        // zoom (RainViewer's coverage tops out at z=10). See rainviewerTile.js
        getTileUrl: (coord, zm) =>
          rainviewerTileUrl(rvHost, f.path, coord.x, coord.y, zm),
        tileSize: new window.google.maps.Size(256, 256),
        opacity: 0, // start hidden — frame index effect below reveals the active one
        name: `rv-radar-${i}`,
      });
      stack.push(layer);
      return layer;
    });
  }, [rvHost, frames]);

  // v2.7.31 · Reveal the active frame, hide all others, when frameIdx or opacity changes
  useEffect(() => {
    if (!overlaysRef.current.length) return;
    overlaysRef.current.forEach((o, i) => {
      try { o.setOpacity(i === frameIdx ? opacity : 0); } catch { /* ignore */ }
    });
  }, [frameIdx, opacity]);

  // v2.7.31 · Play loop — advance one frame per `speedMs`
  useEffect(() => {
    if (!playing || frames.length === 0) return;
    const id = setInterval(() => {
      setFrameIdx((i) => (i + 1) % frames.length);
    }, speedMs);
    return () => clearInterval(id);
  }, [playing, speedMs, frames.length]);

  // Render OK county boundaries (regional only)
  useEffect(() => {
    if (!mapRef.current || !countiesGJ) return;
    // Clear previous
    for (const p of countyLayerRef.current) p.setMap(null);
    countyLayerRef.current = [];
    if (mode !== "regional") return;
    const countiesInWarning = new Set();
    for (const a of alerts) {
      if (!a.areaDesc) continue;
      const parts = a.areaDesc.split(";").map((s) => s.trim().split(",")[0].trim().toLowerCase());
      for (const p of parts) countiesInWarning.add(p);
    }
    for (const feat of countiesGJ.features || []) {
      const name = (feat.properties?.name || "").toLowerCase();
      const inWarning = countiesInWarning.has(name);
      const drawPoly = (coords) => {
        const path = coords.map(([lng, lat]) => ({ lat, lng }));
        const poly = new window.google.maps.Polygon({
          paths: path,
          strokeColor: inWarning ? "#ef4444" : "#52525b",
          strokeWeight: inWarning ? 1.6 : 0.5,
          strokeOpacity: 0.85,
          fillColor: inWarning ? "#ef4444" : "#a1a1aa",
          fillOpacity: inWarning ? 0.18 : 0.02,
          clickable: false,
          map: mapRef.current,
        });
        countyLayerRef.current.push(poly);
      };
      const geom = feat.geometry || {};
      if (geom.type === "Polygon") drawPoly(geom.coordinates[0]);
      else if (geom.type === "MultiPolygon") geom.coordinates.forEach((p) => drawPoly(p[0]));
    }
  }, [countiesGJ, mode, alerts]);

  // Render warning polygons on top of counties — v2.7.32 · NWS standard colors
  useEffect(() => {
    if (!mapRef.current) return;
    for (const p of warningLayerRef.current) p.setMap(null);
    warningLayerRef.current = [];
    for (const a of alerts) {
      const geom = a.geometry;
      if (!geom) continue;
      const stroke = nwsStroke(a.event, a.severity);
      const fill = nwsFill(a.event, a.severity);
      const drawPoly = (coords) => {
        const path = coords.map(([lng, lat]) => ({ lat, lng }));
        const poly = new window.google.maps.Polygon({
          paths: path,
          strokeColor: stroke,
          strokeWeight: 2.5,
          strokeOpacity: 0.95,
          fillColor: fill,
          fillOpacity: 0.22,
          map: mapRef.current,
        });
        warningLayerRef.current.push(poly);
      };
      if (geom.type === "Polygon") drawPoly(geom.coordinates[0]);
      else if (geom.type === "MultiPolygon") geom.coordinates.forEach((p) => drawPoly(p[0]));
    }
  }, [alerts]);

  // v2.7.31 · Fullscreen toggle — wraps the entire RadarMap div so the
  // playback controls remain visible while immersive.
  const toggleFullscreen = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
      if (req) {
        req.call(el).then(() => {
          // Google Maps needs a resize nudge after the container snaps to fullscreen
          setTimeout(() => {
            try { window.google?.maps?.event?.trigger(mapRef.current, "resize"); } catch { /* ignore */ }
          }, 250);
        }).catch(() => {});
      }
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
      if (exit) exit.call(document);
    }
  }, []);
  useEffect(() => {
    const onFs = () => {
      const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
      setFullscreen(isFs);
      // Resize the map after the transition completes
      setTimeout(() => {
        try { window.google?.maps?.event?.trigger(mapRef.current, "resize"); } catch { /* ignore */ }
      }, 250);
    };
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, []);

  // Current frame metadata for the timestamp pill (drives the "Past" vs "Forecast" tag)
  const currentFrame = frames[frameIdx];
  const currentTs = currentFrame?.time ? new Date(currentFrame.time * 1000) : null;
  const isNowcast = currentFrame ? currentFrame.time * 1000 > Date.now() : false;

  return (
    <div
      ref={wrapperRef}
      className={fullscreen ? "fixed inset-0 z-[60] bg-zinc-950" : compact ? "relative w-full h-full" : "relative w-full"}
      data-testid={`radar-wrapper-${mode}`}
    >
      <div
        ref={mapDivRef}
        data-testid={`radar-map-${mode}`}
        className={
          fullscreen
            ? "w-full h-full"
            : compact
              ? "w-full h-full"
              : "w-full h-[640px] rounded-lg overflow-hidden border border-zinc-800"
        }
      />
      {!compact && (
        <RadarControls
          frames={frames}
          frameIdx={frameIdx}
          setFrameIdx={setFrameIdx}
          playing={playing}
          setPlaying={setPlaying}
          speedMs={speedMs}
          setSpeedMs={setSpeedMs}
          opacity={opacity}
          setOpacity={setOpacity}
          fullscreen={fullscreen}
          toggleFullscreen={toggleFullscreen}
          timestamp={currentTs}
          nowcast={isNowcast}
          radarMode={radarMode}
          setRadarMode={setRadarMode}
          hasFuture={framesFuture.length > 0}
        />
      )}
    </div>
  );
}

// v2.7.31 · Radar playback overlay — sits on top of the radar map. All
// controls live inside the fullscreen-target wrapper so they remain
// visible (and operable) while the radar is in immersive mode.
function RadarControls({
  frames, frameIdx, setFrameIdx, playing, setPlaying,
  speedMs, setSpeedMs, opacity, setOpacity,
  fullscreen, toggleFullscreen, timestamp, nowcast,
  radarMode = "live", setRadarMode = () => {}, hasFuture = false,
}) {
  const total = frames.length;
  const stepBack = () => { setPlaying(false); setFrameIdx((i) => (i - 1 + Math.max(1, total)) % Math.max(1, total)); };
  const stepFwd  = () => { setPlaying(false); setFrameIdx((i) => (i + 1) % Math.max(1, total)); };
  return (
    <>
      {/* Fullscreen toggle (top-right) */}
      <button
        type="button"
        onClick={toggleFullscreen}
        className="absolute top-3 right-3 z-30 bg-zinc-900/90 backdrop-blur border border-zinc-700 rounded-lg p-2 text-zinc-200 hover:text-amber-300 hover:border-amber-400/60 transition-colors shadow-lg flex items-center gap-1.5"
        data-testid="radar-fullscreen-btn"
        title={fullscreen ? "Exit fullscreen" : "Fullscreen radar"}
      >
        {fullscreen ? <Minimize2 className="h-4 w-4"/> : <Maximize2 className="h-4 w-4"/>}
        <span className="text-[10px] uppercase tracking-wider font-bold hidden sm:inline">
          {fullscreen ? "Exit" : "Fullscreen"}
        </span>
      </button>

      {/* v2.7.34 · Live ↔ Future Radar toggle (top-left, next to attribution) */}
      <div className="absolute top-3 left-3 z-30 flex items-center gap-2">
        <div className="bg-zinc-900/95 backdrop-blur border border-zinc-700 rounded-lg overflow-hidden flex" data-testid="radar-mode-toggle">
          <button
            type="button"
            onClick={() => setRadarMode("live")}
            className={`px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider transition-colors ${radarMode === "live" ? "bg-sky-400/30 text-sky-100" : "text-zinc-400 hover:text-zinc-200"}`}
            data-testid="radar-mode-live"
          >Live</button>
          <button
            type="button"
            onClick={() => setRadarMode("future")}
            disabled={!hasFuture}
            className={`px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider transition-colors ${radarMode === "future" ? "bg-fuchsia-400/30 text-fuchsia-100" : "text-zinc-400 hover:text-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed"}`}
            data-testid="radar-mode-future"
            title={hasFuture ? "Switch to nowcast / forecast frames" : "No future frames in current manifest"}
          >Future</button>
        </div>
        <div className="bg-zinc-900/80 backdrop-blur border border-zinc-700 rounded px-2 py-1 text-[10px] text-zinc-400 font-bold uppercase tracking-wider pointer-events-none">
          {radarMode === "future" ? "Nowcast · RainViewer · " : "RainViewer · "}{total} frame{total === 1 ? "" : "s"}
        </div>
      </div>

      {/* Playback overlay (bottom) */}
      <div className="absolute bottom-3 left-3 right-3 z-30 bg-zinc-900/95 backdrop-blur border border-zinc-700 rounded-xl shadow-2xl px-3 py-2.5 flex items-center gap-3 flex-wrap">
        {/* Step / play / step */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={stepBack}
            className="w-8 h-8 rounded-md bg-zinc-800/70 border border-zinc-700 text-zinc-300 hover:text-sky-300 hover:border-sky-400/50 transition-colors flex items-center justify-center"
            data-testid="radar-step-back-btn"
            title="Previous frame"
          >
            <SkipBack className="h-3.5 w-3.5"/>
          </button>
          <button
            type="button"
            onClick={() => setPlaying((p) => !p)}
            className="w-10 h-10 rounded-full bg-sky-500/20 border border-sky-400/60 text-sky-200 hover:bg-sky-500/30 hover:border-sky-300 transition-colors flex items-center justify-center shadow-[0_0_18px_rgba(56,189,248,0.25)]"
            data-testid="radar-playpause-btn"
            title={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="h-4 w-4"/> : <Play className="h-4 w-4 ml-0.5"/>}
          </button>
          <button
            type="button"
            onClick={stepFwd}
            className="w-8 h-8 rounded-md bg-zinc-800/70 border border-zinc-700 text-zinc-300 hover:text-sky-300 hover:border-sky-400/50 transition-colors flex items-center justify-center"
            data-testid="radar-step-fwd-btn"
            title="Next frame"
          >
            <SkipForward className="h-3.5 w-3.5"/>
          </button>
        </div>

        {/* Scrub slider */}
        <div className="flex-1 min-w-[200px] flex items-center gap-2">
          <span className="text-[10px] text-zinc-500 tabular-nums w-10 text-right font-mono">
            {total > 0 ? `${frameIdx + 1}/${total}` : "—"}
          </span>
          <input
            type="range"
            min={0}
            max={Math.max(0, total - 1)}
            value={frameIdx}
            onChange={(e) => { setPlaying(false); setFrameIdx(Number(e.target.value)); }}
            className="flex-1 accent-sky-400 cursor-pointer"
            data-testid="radar-scrub-slider"
            disabled={total === 0}
          />
        </div>

        {/* Timestamp + frame type pill */}
        <div className="flex items-center gap-1.5 text-[10px] tabular-nums px-2 py-1 bg-zinc-950 rounded border border-zinc-800 min-w-[120px] justify-center">
          <span className={nowcast ? "text-fuchsia-300 font-extrabold uppercase tracking-wider" : "text-amber-300 font-extrabold uppercase tracking-wider"}>
            {nowcast ? "Forecast" : "Live"}
          </span>
          <span className="text-zinc-300 font-mono" data-testid="radar-timestamp">
            {timestamp ? timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
        </div>

        {/* Speed selector */}
        <div className="flex items-center gap-0.5 bg-zinc-950 rounded p-0.5 border border-zinc-800">
          {[
            { ms: 800, label: "0.5x" },
            { ms: 500, label: "1x" },
            { ms: 250, label: "2x" },
            { ms: 120, label: "4x" },
          ].map((s) => (
            <button
              key={s.ms}
              type="button"
              onClick={() => setSpeedMs(s.ms)}
              className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded transition-colors ${speedMs === s.ms ? "bg-sky-400/30 text-sky-200" : "text-zinc-400 hover:text-zinc-200"}`}
              data-testid={`radar-speed-${s.label}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Opacity slider */}
        <div className="hidden md:flex items-center gap-2 pl-2 border-l border-zinc-700">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Opacity</span>
          <input
            type="range"
            min={0.2}
            max={1}
            step={0.05}
            value={opacity}
            onChange={(e) => setOpacity(Number(e.target.value))}
            className="w-20 accent-sky-400 cursor-pointer"
            data-testid="radar-opacity-slider"
          />
        </div>
      </div>
    </>
  );
}

// ────────────────────── Tab: NWS Discussions ──────────────────────
function DiscussionsPanel() {
  const [items, setItems] = useState([]);
  const [selectedText, setSelectedText] = useState("");
  const [selectedMeta, setSelectedMeta] = useState(null);
  const reload = useCallback(() => {
    adminApi.get("/admin/weather/discussions").then((r) => setItems(r.data?.items || [])).catch(() => {});
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const openProduct = async (it) => {
    setSelectedMeta(it);
    setSelectedText("Loading…");
    try {
      const pid = (it.id || "").split("/").pop();
      const r = await adminApi.get(`/admin/weather/discussions/${pid}`);
      setSelectedText(r.data?.productText || "(no text)");
    } catch (e) {
      setSelectedText(`Fetch failed: ${e?.message || e}`);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-1 max-h-[80vh] overflow-y-auto">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-sky-400/40 flex items-center justify-between">
          <span>OUN · Area Forecast Discussions</span>
          <Button size="sm" variant="ghost" className="h-7 text-amber-300" onClick={reload}><RefreshCw className="h-3.5 w-3.5"/></Button>
        </div>
        {items.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">No products loaded yet.</div>}
        <div className="space-y-1.5">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => openProduct(it)}
              className={`block w-full text-left p-2.5 rounded border ${selectedMeta?.id === it.id ? "border-sky-400/50 bg-sky-400/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"} transition-colors`}
              data-testid={`afd-${it.id?.slice(-12)}`}
            >
              <div className="text-xs font-bold text-zinc-200">{it.productCode} · {it.issuingOffice}</div>
              <div className="text-[10px] text-zinc-500 tabular-nums">{it.issuanceTime?.replace("T", " ").slice(0, 16)}</div>
            </button>
          ))}
        </div>
      </Card>
      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2 max-h-[80vh] overflow-y-auto">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-sky-400/40">
          {selectedMeta ? `${selectedMeta.productCode} · ${selectedMeta.issuanceTime}` : "Select a product"}
        </div>
        <pre className="text-xs font-mono text-zinc-300 whitespace-pre-wrap leading-relaxed">{selectedText || "—"}</pre>
      </Card>
    </div>
  );
}

// ────────────────────── Tab: Active Warnings ──────────────────────
function WarningsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [busyLift, setBusyLift] = useState(null);
  const [filter, setFilter] = useState("all");
  const reload = useCallback(() => {
    adminApi.get("/admin/weather/alerts/active").then((r) => setAlerts(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { reload(); const id = setInterval(reload, 10000); return () => clearInterval(id); }, [reload]);

  const ground = async (alert) => {
    setBusy(alert.id);
    try {
      const r = await adminApi.post(`/admin/weather/${encodeURIComponent(alert.id)}/ground-fleet`);
      toast.success(`Grounded ${r.data?.grounded_count ?? 0} driver(s) in zone`);
    } catch (e) {
      toast.error(`Ground-fleet failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  // v2.7.32 · Resume-Operations override — clear-signals the geofenced
  // zone and pushes a `weather_grounding_lifted` event to all drivers.
  const lift = async (alert) => {
    setBusyLift(alert.id);
    try {
      const r = await adminApi.post(`/admin/weather/${encodeURIComponent(alert.id)}/lift-grounding`);
      toast.success(`Cleared ${r.data?.cleared_count ?? 0} driver(s) — fleet may resume in zone`);
    } catch (e) {
      toast.error(`Resume-Ops failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyLift(null);
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return alerts;
    if (filter === "warnings") return alerts.filter((a) => /Warning/i.test(a.event));
    if (filter === "watches") return alerts.filter((a) => /Watch/i.test(a.event));
    return alerts.filter((a) => a.event === filter);
  }, [alerts, filter]);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 pb-2 mb-3 border-b border-red-400/40">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-red-300"/>Active NWS Alerts · {filtered.length}
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-xs h-8 w-44" data-testid="warnings-filter">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-700">
              <SelectItem value="all">All alerts</SelectItem>
              <SelectItem value="warnings">Warnings only</SelectItem>
              <SelectItem value="watches">Watches only</SelectItem>
              <SelectItem value="Tornado Warning">Tornado Warning</SelectItem>
              <SelectItem value="Severe Thunderstorm Warning">Severe Thunderstorm Warning</SelectItem>
              <SelectItem value="Flash Flood Warning">Flash Flood Warning</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="h-8 text-amber-300" onClick={reload}><RefreshCw className="h-3.5 w-3.5"/></Button>
        </div>
      </div>
      {filtered.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center">No matching alerts.</div>}
      <div className="space-y-3">
        {filtered.map((a) => (
          <div key={a.id} className={`rounded p-4 border ${severityCls(a.event, a.severity)} bg-zinc-950/40`} data-testid={`warning-${a.id?.slice(-12)}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm">{a.event}</span>
                  <Badge className="bg-zinc-950 border-zinc-700 text-[10px] uppercase">{a.severity}</Badge>
                  <Badge className="bg-zinc-950 border-zinc-700 text-[10px] uppercase">{a.urgency}</Badge>
                  <span className="text-[10px] text-zinc-500 tabular-nums">{fmtExpiry(a.expires)}</span>
                </div>
                <div className="text-xs text-zinc-200 mt-2">{a.headline}</div>
                <div className="text-[10px] text-zinc-500 mt-1.5">{a.areaDesc}</div>
                {a.instruction && (
                  <div className="text-[11px] text-zinc-300 mt-2 p-2 bg-zinc-950 border border-zinc-800 rounded whitespace-pre-wrap">{a.instruction.slice(0, 600)}</div>
                )}
              </div>
              <div className="flex flex-col gap-2 items-stretch min-w-[200px]">
                <Button
                  onClick={() => ground(a)}
                  disabled={busy === a.id}
                  className="bg-red-500/20 text-red-200 hover:bg-red-500/30 border border-red-400/40 text-xs h-9 font-bold uppercase tracking-wider"
                  data-testid={`ground-${a.id?.slice(-12)}`}
                >
                  {busy === a.id ? "Grounding…" : "Ground Fleet in Zone"}
                </Button>
                <Button
                  onClick={() => lift(a)}
                  disabled={busyLift === a.id}
                  className="bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 border border-emerald-400/50 text-xs h-9 font-bold uppercase tracking-wider"
                  data-testid={`lift-${a.id?.slice(-12)}`}
                  title="Authorised manual override — lifts the grounding mandate and pushes a clear-signal to all drivers in the polygon."
                >
                  <ShieldCheck className="h-3.5 w-3.5 mr-1.5"/>
                  {busyLift === a.id ? "Clearing…" : "Allow Fleet in Zone"}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ────────────────────── Tab: Dispatch Warning ──────────────────────
function DispatchWarningPanel() {
  const [audience, setAudience] = useState("drivers");
  const [severity, setSeverity] = useState("warning");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  // v2.7.34 · Send-mode + multi-county zone targeting
  const [sendMode, setSendMode] = useState("all"); // "all" | "zones"
  const [zones, setZones] = useState([]); // selected county names
  const [counties, setCounties] = useState([]); // list of available OK counties
  useEffect(() => {
    adminApi.get("/admin/weather/zones/counties").then((r) => setCounties(r.data?.counties || [])).catch(() => {});
  }, []);

  const toggleCounty = (c) => {
    setZones((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  };
  const selectAllCounties = () => setZones(counties.slice());
  const clearCounties = () => setZones([]);

  const send = async () => {
    if (!message.trim()) {
      toast.error("Message body cannot be empty");
      return;
    }
    if (sendMode === "zones" && zones.length === 0) {
      toast.error("Select at least one county zone, or switch to Send to All");
      return;
    }
    setBusy(true);
    try {
      const body = {
        audience,
        severity,
        message: message.trim(),
        county_filter: sendMode === "zones" ? zones : [],
      };
      const r = await adminApi.post("/admin/weather/dispatch-warning", body);
      const tag = sendMode === "zones" ? `${zones.length} zone${zones.length === 1 ? "" : "s"} · ${r.data?.resolved_driver_count ?? 0} driver(s) in zone` : "platform-wide";
      toast.success(`Broadcast pushed (${tag}) → ${(r.data?.sent_to || []).join(", ")}`);
      setMessage("");
    } catch (e) {
      toast.error(`Push failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-6 max-w-3xl" data-testid="dispatch-warning-panel">
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-4 border-b border-amber-400/40 flex items-center gap-2">
        <Send className="h-3.5 w-3.5 text-amber-300"/>Manual Dispatch Warning Console
      </div>
      <div className="space-y-4">
        {/* v2.7.34 · Send-mode toggle */}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Targeting</label>
          <div className="flex gap-2" data-testid="dispatch-send-mode">
            <button
              type="button"
              onClick={() => setSendMode("all")}
              className={`flex-1 px-3 py-2 rounded text-xs font-extrabold uppercase tracking-wider transition-colors border ${sendMode === "all" ? "bg-amber-400/20 text-amber-200 border-amber-400/60" : "bg-zinc-950 text-zinc-400 border-zinc-700 hover:text-zinc-200"}`}
              data-testid="dispatch-mode-all"
            >Send to All · platform-wide</button>
            <button
              type="button"
              onClick={() => setSendMode("zones")}
              className={`flex-1 px-3 py-2 rounded text-xs font-extrabold uppercase tracking-wider transition-colors border ${sendMode === "zones" ? "bg-sky-400/20 text-sky-200 border-sky-400/60" : "bg-zinc-950 text-zinc-400 border-zinc-700 hover:text-zinc-200"}`}
              data-testid="dispatch-mode-zones"
            >Send by Zone · select counties</button>
          </div>
        </div>

        {/* Multi-county picker — only when zones mode */}
        {sendMode === "zones" && (
          <div data-testid="dispatch-county-picker">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
                Counties ({zones.length} selected · {counties.length} total)
              </label>
              <div className="flex gap-1.5 text-[10px]">
                <button type="button" onClick={selectAllCounties} className="text-sky-300 hover:text-sky-200 font-bold uppercase tracking-wider" data-testid="dispatch-zones-all">All 77</button>
                <span className="text-zinc-700">·</span>
                <button type="button" onClick={clearCounties} className="text-zinc-400 hover:text-zinc-200 font-bold uppercase tracking-wider" data-testid="dispatch-zones-clear">Clear</button>
              </div>
            </div>
            <div className="bg-zinc-950 border border-zinc-700 rounded p-2 max-h-56 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1">
              {counties.map((c, i) => {
                const on = zones.includes(c);
                // v2.7.35 · P2 fix · Stable slug guarantees 77 unique testids
                // even if a county name slugifies to an empty string (defensive).
                const slug = (c || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || `idx${i}`;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCounty(c)}
                    className={`text-left text-[11px] px-2 py-1 rounded transition-colors ${on ? "bg-sky-400/30 text-sky-100 font-bold" : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"}`}
                    data-testid={`dispatch-zone-${slug}`}
                    data-zone-county={c}
                    data-zone-index={i}
                  >
                    <input type="checkbox" checked={on} onChange={() => {}} className="mr-1.5 accent-sky-400"/>{c}
                  </button>
                );
              })}
            </div>
            {zones.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1" data-testid="dispatch-zone-chips">
                {zones.map((c) => (
                  <Badge key={c} className="bg-sky-500/20 text-sky-100 border-sky-400/60 text-[10px] font-bold cursor-pointer" onClick={() => toggleCounty(c)} title="Click to remove">
                    {c} ×
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Audience</label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 h-9 text-sm" data-testid="dispatch-audience">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-700">
                <SelectItem value="drivers">All Drivers</SelectItem>
                <SelectItem value="riders">All Riders</SelectItem>
                <SelectItem value="both">Drivers + Riders</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Severity</label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 h-9 text-sm" data-testid="dispatch-severity">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-700">
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Message body</label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Tornado on the ground near Weatherford — all drivers shelter at the nearest underground location immediately."
            className="bg-zinc-950 border-zinc-700 min-h-[120px] text-sm font-mono"
            data-testid="dispatch-message"
            maxLength={600}
          />
          <div className="text-[10px] text-zinc-500 mt-1 text-right tabular-nums">{message.length}/600</div>
        </div>
        <Button
          onClick={send}
          disabled={busy || !message.trim()}
          className="w-full bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold"
          data-testid="dispatch-send"
        >
          <Send className="h-4 w-4 mr-2"/>
          {busy ? "Pushing…" : sendMode === "zones" ? `Broadcast to ${zones.length} zone${zones.length === 1 ? "" : "s"}` : "Broadcast to all"}
        </Button>
      </div>
    </Card>
  );
}

// ──────────────── AFD City + County Auto-Extractor ────────────────
// v2.7.33 · Spec section 2: "Every individual discussion segment must
// identify and dynamically display relevant City Names and County Names
// directly at the top of the parsed text block."
//
// NWS AFD products contain city + county mentions in two predictable
// spots:
//   1. The .OUN WATCHES/WARNINGS/ADVISORIES... section lists counties.
//   2. The .PRELIMINARY POINT TEMPS/POPS... section lists cities.
// Plus the body text may mention either. We do a lightweight scan that
// matches against a known whitelist of OK cities + counties — false
// positives are essentially zero with that approach.
const OK_CITY_WHITELIST = [
  "Oklahoma City", "Tulsa", "Norman", "Broken Arrow", "Lawton", "Edmond",
  "Moore", "Midwest City", "Enid", "Stillwater", "Muskogee", "Bartlesville",
  "Owasso", "Shawnee", "Yukon", "Ardmore", "Ponca City", "Bixby", "Duncan",
  "Del City", "Sapulpa", "Sand Springs", "Bethany", "Mustang", "Altus",
  "El Reno", "Durant", "McAlester", "Claremore", "Weatherford", "Chickasha",
  "Miami", "Choctaw", "Glenpool", "Woodward", "Elk City", "Guymon",
  "Tahlequah", "Pryor", "Newcastle", "OKC", "Tinker AFB", "Vance AFB",
  "Fort Sill", "Will Rogers",
];
const OK_COUNTY_WHITELIST = [
  "Adair", "Alfalfa", "Atoka", "Beaver", "Beckham", "Blaine", "Bryan",
  "Caddo", "Canadian", "Carter", "Cherokee", "Choctaw", "Cimarron",
  "Cleveland", "Coal", "Comanche", "Cotton", "Craig", "Creek", "Custer",
  "Delaware", "Dewey", "Ellis", "Garfield", "Garvin", "Grady", "Grant",
  "Greer", "Harmon", "Harper", "Haskell", "Hughes", "Jackson", "Jefferson",
  "Johnston", "Kay", "Kingfisher", "Kiowa", "Latimer", "Le Flore",
  "Lincoln", "Logan", "Love", "Major", "Marshall", "Mayes", "McClain",
  "McCurtain", "McIntosh", "Murray", "Muskogee", "Noble", "Nowata",
  "Okfuskee", "Oklahoma", "Okmulgee", "Osage", "Ottawa", "Pawnee",
  "Payne", "Pittsburg", "Pontotoc", "Pottawatomie", "Pushmataha",
  "Roger Mills", "Rogers", "Seminole", "Sequoyah", "Stephens", "Texas",
  "Tillman", "Tulsa", "Wagoner", "Washington", "Washita", "Woods", "Woodward",
];

function extractAFDTargets(text) {
  if (!text) return { cities: [], counties: [] };
  const upper = text.toUpperCase();
  const cities = OK_CITY_WHITELIST.filter((c) => upper.includes(c.toUpperCase()));
  const counties = OK_COUNTY_WHITELIST.filter((c) => upper.includes(c.toUpperCase()));
  return { cities, counties };
}

function AFDExtractedTargets({ text }) {
  const { cities, counties } = useMemo(() => extractAFDTargets(text), [text]);
  if (cities.length === 0 && counties.length === 0) return null;
  return (
    <div className="mb-3 pb-3 border-b border-zinc-800" data-testid="afd-extracted-targets">
      {cities.length > 0 && (
        <div className="mb-2">
          <div className="text-[9px] uppercase tracking-widest text-sky-400 font-bold mb-1.5">Cities mentioned · {cities.length}</div>
          <div className="flex flex-wrap gap-1.5">
            {cities.map((c) => (
              <Badge key={c} className="bg-sky-500/15 text-sky-200 border-sky-400/40 text-[10px] font-semibold" data-testid={`afd-city-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</Badge>
            ))}
          </div>
        </div>
      )}
      {counties.length > 0 && (
        <div>
          <div className="text-[9px] uppercase tracking-widest text-amber-400 font-bold mb-1.5">Counties mentioned · {counties.length}</div>
          <div className="flex flex-wrap gap-1.5">
            {counties.map((c) => (
              <Badge key={c} className="bg-amber-500/15 text-amber-200 border-amber-400/40 text-[10px] font-semibold" data-testid={`afd-county-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c} County</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────── Tab: NWS Forecast Discussions (structured) ────────────────
// v2.7.33 · Renamed from "OUN Area Forecast Discussion" per spec. The
// structured view extracts City + County mentions from each AFD product
// and shows them as a chip strip at the top of every product card. The
// raw AFD body is shown below for operators who need the verbatim text.
function OUNAreaForecastPanel() {
  const [alerts, setAlerts] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedText, setSelectedText] = useState("");
  const [selectedMeta, setSelectedMeta] = useState(null);

  const reload = useCallback(() => {
    adminApi.get("/admin/weather/alerts/active").then((r) => setAlerts(r.data || [])).catch(() => {});
    adminApi.get("/admin/weather/discussions").then((r) => setProducts(r.data?.items || [])).catch(() => {});
  }, []);
  // v2.7.33 · 10-second polling per spec section 4
  useEffect(() => { reload(); const id = setInterval(reload, 10000); return () => clearInterval(id); }, [reload]);

  // Open the most recent AFD by default
  useEffect(() => {
    if (!selectedMeta && products.length > 0) {
      openProduct(products[0]);
    }
  }, [products, selectedMeta]);

  const openProduct = async (p) => {
    setSelectedMeta(p);
    setSelectedText("Loading…");
    try {
      const pid = (p.id || "").split("/").pop();
      const r = await adminApi.get(`/admin/weather/discussions/${pid}`);
      setSelectedText(r.data?.productText || "(no text)");
    } catch (e) {
      setSelectedText(`Fetch failed: ${e?.message || e}`);
    }
  };

  // Group active alerts by extracted city name. NWS `areaDesc` is a
  // semicolon-separated list ("Custer, OK; Beckham, OK; Washita, OK").
  // We split on `;`, trim, drop the state suffix, and group all alerts
  // whose areaDesc mentions the city.
  const cityGroups = useMemo(() => {
    const map = new Map();
    for (const a of alerts) {
      const parts = (a.areaDesc || "").split(";").map((s) => s.trim().split(",")[0].trim());
      for (const c of parts) {
        if (!c) continue;
        if (!map.has(c)) map.set(c, []);
        map.get(c).push(a);
      }
    }
    // Sort: cities with WARNING first, then WATCH, then alphabetical
    return [...map.entries()]
      .map(([city, list]) => {
        const hasWarning = list.some((a) => isWarning(a.event));
        const hasWatch = list.some((a) => isWatch(a.event));
        return { city, list, hasWarning, hasWatch };
      })
      .sort((a, b) => {
        if (a.hasWarning !== b.hasWarning) return a.hasWarning ? -1 : 1;
        if (a.hasWatch !== b.hasWatch) return a.hasWatch ? -1 : 1;
        return a.city.localeCompare(b.city);
      });
  }, [alerts]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="oun-afd-panel">
      {/* Left rail · product picker */}
      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-1 max-h-[80vh] overflow-y-auto">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-sky-400/40 flex items-center justify-between">
          <span className="flex items-center gap-2"><BookOpen className="h-3.5 w-3.5 text-sky-300"/>OUN · Norman, OK · AFD Archive</span>
          <Button size="sm" variant="ghost" className="h-7 text-amber-300" onClick={reload}><RefreshCw className="h-3.5 w-3.5"/></Button>
        </div>
        {products.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">No AFD products loaded yet.</div>}
        <div className="space-y-1.5">
          {products.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => openProduct(p)}
              className={`block w-full text-left p-2.5 rounded border transition-colors ${selectedMeta?.id === p.id ? "border-sky-400/60 bg-sky-400/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"}`}
              data-testid={`oun-afd-row-${p.id?.slice(-12)}`}
            >
              <div className="text-xs font-bold text-zinc-200">{p.productCode} · {p.issuingOffice}</div>
              <div className="text-[10px] text-zinc-500 tabular-nums">{p.issuanceTime?.replace("T", " ").slice(0, 16)}</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Right · city-grouped status cards + raw AFD text */}
      <div className="lg:col-span-2 space-y-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-amber-400/40 flex items-center justify-between">
            <span>City-Level Status Map · OUN Forecast Area</span>
            <Badge className="bg-zinc-950 border-zinc-700 text-zinc-300">{cityGroups.length} cit{cityGroups.length === 1 ? "y" : "ies"}</Badge>
          </div>
          {cityGroups.length === 0 && (
            <div className="text-sm text-zinc-500 py-4 text-center" data-testid="oun-afd-no-cities">
              No active warnings or watches in the OUN forecast area. All cities currently clear.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {cityGroups.map(({ city, list, hasWarning, hasWatch }) => (
              <div
                key={city}
                className={`rounded-lg p-3.5 border-l-4 bg-zinc-950/60 ${hasWarning ? "border-l-red-500 border border-red-500/40" : hasWatch ? "border-l-yellow-400 border border-yellow-400/40" : "border-l-zinc-700 border border-zinc-800"}`}
                data-testid={`oun-city-${city.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-lg font-extrabold text-zinc-100 leading-tight">{city}</div>
                  {hasWarning ? (
                    <Badge className="bg-red-500/25 text-red-100 border-red-400/70 font-extrabold uppercase tracking-wider text-[10px]" data-testid={`oun-status-${city.toLowerCase().replace(/\s+/g, "-")}`}>
                      Warning
                    </Badge>
                  ) : hasWatch ? (
                    <Badge className="bg-yellow-400/25 text-yellow-100 border-yellow-300/70 font-extrabold uppercase tracking-wider text-[10px]" data-testid={`oun-status-${city.toLowerCase().replace(/\s+/g, "-")}`}>
                      Watch
                    </Badge>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  {list.map((a) => {
                    const info = NWS_INFO(a.event, a.severity);
                    return (
                      <div key={a.id} className={`text-[11px] rounded px-2 py-1.5 border ${info.tw.bg} ${info.tw.text} ${info.tw.border}`}>
                        <div className="font-bold">{a.event}</div>
                        <div className="opacity-80 mt-0.5 line-clamp-2">{a.headline}</div>
                        <div className="opacity-60 mt-0.5 text-[10px]">{fmtExpiry(a.expires)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Raw AFD text — collapsed below the structured view */}
        <Card className="bg-zinc-900 border-zinc-800 p-5 max-h-[60vh] overflow-y-auto">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-sky-400/40">
            {selectedMeta ? `${selectedMeta.productCode} · ${selectedMeta.issuanceTime}` : "Select an AFD product on the left"}
          </div>
          {selectedText && selectedText !== "Loading…" && selectedText !== "—" && (
            <AFDExtractedTargets text={selectedText}/>
          )}
          <pre className="text-xs font-mono text-zinc-300 whitespace-pre-wrap leading-relaxed" data-testid="oun-afd-product-text">{selectedText || "—"}</pre>
        </Card>
      </div>
    </div>
  );
}

// ──────────────── Tab: State Emergencies (embedded) ────────────────
// v2.7.32 · The standalone StateEmergenciesPage is now also accessible
// as a tab inside Weather Commander per the unified nav restructure.
function StateEmergenciesEmbed() {
  return (
    <div data-testid="state-emergencies-embed" className="-mx-4 sm:-mx-6 -my-4 sm:-my-6">
      <StateEmergenciesPage embedded/>
    </div>
  );
}
